import React from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

// Copy bằng textarea ẩn — fallback cho trình duyệt không có
// navigator.clipboard (context không secure, Safari cũ, v.v.)
function fallbackCopy(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(textarea);
    }
}

/**
 * Copy mã + toast — dùng chung cho chip rời (field discountCode) và chip
 * inline (#MÃ trong nội dung bài). Export để community-linkified-text tái dùng.
 */
export async function copyDiscountCode(code) {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(code);
        } else {
            fallbackCopy(code);
        }
        toast.success('Đã copy mã giảm giá!');
    } catch (error) {
        toast.error('Không thể copy mã, vui lòng thử lại.');
    }
}

/**
 * Chip mã INLINE nằm giữa dòng text nội dung bài — user gõ #SHPCCCC trong
 * bài là đoạn đó thành nút vàng bấm-copy (copy mã không kèm dấu #).
 */
export function CommunityInlineCodeChip({ code }) {
    return (
        <button
            type="button"
            title="Bấm để copy mã"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copyDiscountCode(code);
            }}
            className="inline-flex items-center gap-1 px-1.5 py-px mx-0.5 rounded-md bg-yellow-400 text-yellow-900 font-bold text-[13px] align-baseline hover:bg-yellow-300 active:scale-[0.98] transition-all"
        >
            {code}
            <FontAwesomeIcon icon={faCopy} className="text-[10px]" />
        </button>
    );
}

/**
 * Field discountCode có thể chứa NHIỀU mã ngăn bởi dấu phẩy (backend chuẩn hoá
 * ở services/community-post-helpers.js) — tách ra để mỗi mã 1 chip copy riêng.
 */
export function parseDiscountCodes(raw) {
    if (!raw) return [];
    return String(raw)
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean);
}

/**
 * Chip mã giảm giá vàng nổi bật, tap để copy — dùng chung cho card feed +
 * trang chi tiết bài viết Kênh Cộng Đồng. `stopPropagation` để không mở
 * trang chi tiết khi tap chip trong danh sách feed (card cha có thể có click
 * điều hướng bọc ngoài).
 */
// Bài kèm cả chùm mã (tối đa 10) sẽ đẩy ảnh/nội dung xuống quá sâu trên feed —
// hiện 4 mã đầu, còn lại gom vào nút "+N mã" bấm mới bung.
const VISIBLE_CODES = 4;

function CommunityDiscountChip({ code }) {
    const codes = parseDiscountCodes(code);
    const [expanded, setExpanded] = React.useState(false);
    if (codes.length === 0) return null;

    const handleCopy = (value) => async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await copyDiscountCode(value);
    };

    const hidden = expanded ? 0 : Math.max(0, codes.length - VISIBLE_CODES);
    const shown = hidden ? codes.slice(0, VISIBLE_CODES) : codes;

    // Nhiều mã: mỗi mã 1 nút riêng, chỉ nút ĐẦU mới ghi nhãn "Mã:" cho gọn
    return (
        <div className="flex flex-wrap items-center gap-2">
            {shown.map((value, index) => (
                <button
                    key={value}
                    type="button"
                    onClick={handleCopy(value)}
                    title="Bấm để copy mã"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-400 text-yellow-900 font-bold text-sm shadow-sm hover:bg-yellow-300 active:scale-[0.98] transition-all"
                >
                    <span>{index === 0 ? `Mã: ${value}` : value}</span>
                    <FontAwesomeIcon icon={faCopy} className="text-xs" />
                </button>
            ))}
            {hidden > 0 && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setExpanded(true);
                    }}
                    className="px-2.5 py-1.5 rounded-lg border border-yellow-400 text-yellow-700 font-semibold text-sm hover:bg-yellow-50 active:scale-[0.98] transition-all"
                >
                    +{hidden} mã
                </button>
            )}
        </div>
    );
}

export default CommunityDiscountChip;
